import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Copy,
  Download,
  Eye,
  FileDown,
  FilePlus,
  FileSpreadsheet,
  Loader2,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import {
  payrollReportTemplatesApi,
  payrollDetailsReportRunsApi,
  payrollRunApi,
  type PayrollReportTemplateDto,
} from '../services/api';
import { buildEmployeeLookupMaps, loadPayrollReportEmployees } from '../utils/payrollReportBootstrap';
import type { PayrollRun, PayrollRunPreviewDto } from '../types';
import {
  type PayrollReportLayout,
  type PayrollReportFilters,
  defaultPayrollFilters,
  parsePayrollFiltersFromJson,
  buildPayrollReportRows,
  formatPayrollReportCell,
  catalogFieldsForLayout,
  defaultOutputKeysForLayout,
  getDefaultLabelForPayrollOutputField,
  uniqueLineCategories,
  displayLineCategory,
  resolvePayPeriodType,
  resolvePayPeriodLabel,
  formatPayrollRunPeriodRow,
  PAYROLL_REPORT_OUTPUT_FIELD_CATALOG,
  type PayrollReportDataRow,
  type PayrollLineBreakdown,
} from '../utils/payrollReportOutputCatalog';
import { createCSVFile, createExcelFile, createPDFFile } from '../utils/reportExportFiles';
import { formatCompanyDateTime } from '../utils/companyDateFormat';
import NamePromptModal from '../components/NamePromptModal';
import MessageDialog, { type MessageDialogOpen } from '../components/MessageDialog';
import type { ReportHistoryItem } from '../components/ReportHistory';
import ReportRunStatusBadge from '../components/ReportRunStatusBadge';

export interface PayrollDetailsReportRunPayload {
  headers: string[];
  rows: string[][];
  recordsProcessed: number;
  parameters: Record<string, unknown>;
  csvUrl?: string;
  excelUrl?: string;
  pdfUrl?: string;
}

interface PayrollDetailsReportProps {
  /** @deprecated Use lifecycle callbacks for background runs. */
  onReportRun?: (payload: PayrollDetailsReportRunPayload) => void | Promise<void>;
  onReportRunQueued?: (item: ReportHistoryItem) => void;
  onReportRunPatch?: (localId: string, patch: Partial<ReportHistoryItem>) => void;
  onReportRunComplete?: (localId: string, payload: PayrollDetailsReportRunPayload) => Promise<void>;
  runHistory?: ReportHistoryItem[];
  onDeleteReport?: (reportId: string) => void;
  onClearAllHistory?: () => void | Promise<void>;
  initialPayrollRunId?: number | null;
}

interface OutputColumnRow {
  id: string;
  fieldKey: string;
  headerLabel: string;
}

type PayrollHistorySnapshot = { headers: string[]; dataRows: string[][] };

type PayrollHistoryItem = ReportHistoryItem & {
  serverPayrollDetailsRunId?: number;
  payrollDetailsSnapshot?: PayrollHistorySnapshot;
};

function newRowId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

function parseColumnsFromJson(json: string): { fieldKey: string; headerLabel: string }[] {
  try {
    const a = JSON.parse(json) as { fieldKey: string; headerLabel: string }[];
    return Array.isArray(a) ? a : [];
  } catch {
    return [];
  }
}

function defaultOutputColumns(layout: PayrollReportLayout): OutputColumnRow[] {
  return defaultOutputKeysForLayout(layout).map((key) => ({
    id: newRowId(),
    fieldKey: key,
    headerLabel: getDefaultLabelForPayrollOutputField(key),
  }));
}

function normalizeLayout(v: string | undefined): PayrollReportLayout {
  return v === 'summary' ? 'summary' : 'detail';
}

function formatRunLabel(r: PayrollRun): string {
  const status = r.status ?? '';
  const period = formatPayrollRunPeriodRow(r);
  return `#${r.id} · ${period}${status ? ` · ${status}` : ''}`;
}

function exportBaseFilename(): string {
  return `Payroll_Details_Report_${new Date().toISOString().slice(0, 10)}`;
}

function payrollExportBase(r: ReportHistoryItem): string {
  const d = new Date(r.createdAt);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `Payroll_Details_Report_${y}-${m}-${day}`;
}

function formatPayrollRunSummary(r: ReportHistoryItem): string {
  const p = r.parameters ?? {};
  const n = r.recordsProcessed ?? p.recordsProcessed ?? 0;
  const cols = p.outputColumnCount ?? 0;
  return `${n} row${n === 1 ? '' : 's'} · ${cols} col${cols === 1 ? '' : 's'}`;
}

function getPayrollSnapshot(r: PayrollHistoryItem): PayrollHistorySnapshot | null {
  const snap = r.payrollDetailsSnapshot;
  if (snap?.headers?.length && Array.isArray(snap.dataRows)) return snap;
  const p = r.parameters as { snapshot?: PayrollHistorySnapshot };
  if (p?.snapshot?.headers?.length && Array.isArray(p.snapshot.dataRows)) return p.snapshot;
  return null;
}

function revokePayrollBlobUrls(r: PayrollHistoryItem | null): void {
  if (!r) return;
  [r.csvUrl, r.excelUrl, r.pdfUrl].forEach((u) => {
    if (u?.startsWith('blob:')) URL.revokeObjectURL(u);
  });
}

const PayrollDetailsReport: React.FC<PayrollDetailsReportProps> = ({
  onReportRun,
  onReportRunQueued,
  onReportRunPatch,
  onReportRunComplete,
  runHistory = [],
  onDeleteReport,
  onClearAllHistory,
  initialPayrollRunId = null,
}) => {
  const [loading, setLoading] = useState(true);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [payrollRuns, setPayrollRuns] = useState<PayrollRun[]>([]);
  const [worksNumberByEmployeeId, setWorksNumberByEmployeeId] = useState<Record<number, string>>({});
  const [firstNameByEmployeeId, setFirstNameByEmployeeId] = useState<Record<number, string>>({});
  const [lastNameByEmployeeId, setLastNameByEmployeeId] = useState<Record<number, string>>({});

  const [layout, setLayout] = useState<PayrollReportLayout>('detail');
  const [filters, setFilters] = useState<PayrollReportFilters>(() => ({
    ...defaultPayrollFilters(),
    payrollRunId: initialPayrollRunId ?? null,
  }));
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [columnsExpanded, setColumnsExpanded] = useState(true);
  const [outputColumns, setOutputColumns] = useState<OutputColumnRow[]>(() => defaultOutputColumns('detail'));
  const [addFieldKey, setAddFieldKey] = useState('');

  const [templateList, setTemplateList] = useState<{ id: number; name: string; isDefault?: boolean }[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [loadedTemplateId, setLoadedTemplateId] = useState<number | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templateIsDefault, setTemplateIsDefault] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const loadedTemplateIdRef = useRef<number | null>(null);
  loadedTemplateIdRef.current = loadedTemplateId;

  const [previewData, setPreviewData] = useState<PayrollRunPreviewDto | null>(null);
  const [previewRows, setPreviewRows] = useState<PayrollReportDataRow[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [runBusy, setRunBusy] = useState(false);

  const [nameModal, setNameModal] = useState<{
    title: string;
    confirmLabel?: string;
    initialValue?: string;
    onConfirm: (name: string) => void | Promise<void>;
  } | null>(null);
  const [messageDialog, setMessageDialog] = useState<MessageDialogOpen>(null);
  const [detailHistoryReport, setDetailHistoryReport] = useState<PayrollHistoryItem | null>(null);
  const [rowExportBusyId, setRowExportBusyId] = useState<string | null>(null);

  const adHoc = loadedTemplateId == null;
  const showSave = adHoc;
  const showSaveAsNew = loadedTemplateId != null;

  const selectedRun = useMemo(
    () => payrollRuns.find((r) => r.id === filters.payrollRunId) ?? null,
    [payrollRuns, filters.payrollRunId]
  );

  const payPeriodType = useMemo(() => resolvePayPeriodType(selectedRun), [selectedRun]);
  const payPeriodLabel = useMemo(() => resolvePayPeriodLabel(selectedRun), [selectedRun]);

  const lineCategories = useMemo(() => uniqueLineCategories(previewData), [previewData]);

  const addableFields = useMemo(() => {
    const used = new Set(outputColumns.map((c) => c.fieldKey));
    return catalogFieldsForLayout(layout).filter((f) => !used.has(f.key));
  }, [layout, outputColumns]);

  const sortedRunHistory = useMemo(
    () => [...runHistory].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [runHistory]
  );

  const showAlert = (title: string, message: string, tone?: 'info' | 'success' | 'error') => {
    setMessageDialog({ variant: 'alert', title, message, tone });
  };

  const showConfirm = (
    title: string,
    message: string,
    onConfirm: () => void,
    options?: { confirmLabel?: string; cancelLabel?: string; danger?: boolean }
  ) => {
    setMessageDialog({
      variant: 'confirm',
      title,
      message,
      onConfirm,
      confirmLabel: options?.confirmLabel ?? 'Delete',
      cancelLabel: options?.cancelLabel,
      tone: options?.danger ? 'danger' : 'default',
    });
  };

  const columnsPayload = useCallback(
    () => outputColumns.map((c) => ({ fieldKey: c.fieldKey, headerLabel: c.headerLabel })),
    [outputColumns]
  );

  const filtersPayload = useCallback(
    () => ({
      payrollRunId: filters.payrollRunId,
      onlyWarnings: filters.onlyWarnings,
      onlyErrors: filters.onlyErrors,
      lineCategories: filters.lineCategories,
      lineBreakdown: filters.lineBreakdown,
      groupByEmployee: filters.groupByEmployee,
    }),
    [filters]
  );

  const refreshTemplates = useCallback(async () => {
    const rows = await payrollReportTemplatesApi.list();
    setTemplateList(rows.map((r) => ({ id: r.id, name: r.name, isDefault: r.isDefault })));
  }, []);

  const applyTemplateDto = useCallback((d: PayrollReportTemplateDto) => {
    const nextLayout = normalizeLayout(d.layout);
    setLayout(nextLayout);
    setFilters(parsePayrollFiltersFromJson(d.filtersJson));
    const cols = parseColumnsFromJson(d.columnsJson);
    setOutputColumns(
      cols.length > 0
        ? cols.map((c) => ({
            id: newRowId(),
            fieldKey: c.fieldKey,
            headerLabel: c.headerLabel || getDefaultLabelForPayrollOutputField(c.fieldKey),
          }))
        : defaultOutputColumns(nextLayout)
    );
    setTemplateName(d.name);
    setTemplateIsDefault(Boolean(d.isDefault));
    setLoadedTemplateId(d.id);
    setSelectedTemplateId(String(d.id));
    setPreviewData(null);
    setPreviewRows(null);
  }, []);

  const loadPreviewForRun = useCallback(async (runId: number): Promise<PayrollRunPreviewDto> => {
    setPreviewLoading(true);
    try {
      const p = await payrollRunApi.preview(runId);
      setPreviewData(p);
      return p;
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [runs, templates] = await Promise.all([
          payrollRunApi.list(80),
          payrollReportTemplatesApi.list(),
        ]);
        if (cancelled) return;
        setPayrollRuns(runs);
        setTemplateList(templates.map((r) => ({ id: r.id, name: r.name, isDefault: r.isDefault })));
        if (initialPayrollRunId != null && runs.some((r) => r.id === initialPayrollRunId)) {
          setFilters((prev) => ({ ...prev, payrollRunId: initialPayrollRunId }));
        }
        const defaultTemplate = templates.find((t) => t.isDefault);
        if (defaultTemplate) {
          try {
            const full = await payrollReportTemplatesApi.get(defaultTemplate.id);
            if (!cancelled) applyTemplateDto(full);
          } catch (e) {
            console.error(e);
          }
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setPreviewError('Failed to load payroll runs.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [initialPayrollRunId]);

  useEffect(() => {
    if (filters.payrollRunId == null) {
      setPreviewData(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const employees = await loadPayrollReportEmployees();
        if (cancelled) return;
        const maps = buildEmployeeLookupMaps(employees);
        setWorksNumberByEmployeeId(maps.worksNumberByEmployeeId);
        setFirstNameByEmployeeId(maps.firstNameByEmployeeId);
        setLastNameByEmployeeId(maps.lastNameByEmployeeId);
        await loadPreviewForRun(filters.payrollRunId!);
      } catch (e) {
        if (!cancelled) console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filters.payrollRunId, loadPreviewForRun]);

  const handleLayoutChange = (next: PayrollReportLayout) => {
    if (next === layout) return;
    setLayout(next);
    setOutputColumns(defaultOutputColumns(next));
    setAddFieldKey('');
    setPreviewRows(null);
  };

  const toggleLineCategory = (cat: string) => {
    setFilters((prev) => {
      const set = new Set(prev.lineCategories);
      if (set.has(cat)) set.delete(cat);
      else set.add(cat);
      return { ...prev, lineCategories: Array.from(set) };
    });
  };

  const buildReportRows = useCallback(
    (preview: PayrollRunPreviewDto) => buildPayrollReportRows(preview, layout, filters, selectedRun),
    [layout, filters, selectedRun]
  );

  const buildExportTable = useCallback(
    (rows: PayrollReportDataRow[]) => {
      const headers = outputColumns.map(
        (col) => col.headerLabel.trim() || getDefaultLabelForPayrollOutputField(col.fieldKey)
      );
      let previousEmployeeId: number | null = null;
      const dataRows = rows.map((row) => {
        const cells = outputColumns.map((col) =>
          String(
            formatPayrollReportCell(row, col.fieldKey, {
              preview: previewData!,
              runStatus: selectedRun?.status ?? null,
              worksNumberByEmployeeId,
              firstNameByEmployeeId,
              lastNameByEmployeeId,
              payPeriodType,
              payPeriodLabel,
              groupByEmployee: filters.groupByEmployee,
              previousEmployeeId,
            }) ?? ''
          )
        );
        previousEmployeeId = row.employee.employeeId;
        return cells;
      });
      return { headers, dataRows };
    },
    [
      outputColumns,
      previewData,
      selectedRun?.status,
      worksNumberByEmployeeId,
      firstNameByEmployeeId,
      lastNameByEmployeeId,
      payPeriodType,
      payPeriodLabel,
      filters.groupByEmployee,
    ]
  );

  const validateBeforeReport = (): string | null => {
    if (filters.payrollRunId == null) return 'Select a payroll run before previewing or running the report.';
    if (outputColumns.length === 0) return 'Add at least one output column.';
    return null;
  };

  const handlePreviewReport = async () => {
    setPreviewError(null);
    const err = validateBeforeReport();
    if (err) {
      showAlert('Cannot preview', err, 'error');
      return;
    }
    try {
      const preview = previewData ?? (await loadPreviewForRun(filters.payrollRunId!));
      const rows = buildReportRows(preview);
      setPreviewRows(rows);
      setPreviewModalOpen(true);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Preview failed.';
      setPreviewError(msg);
      showAlert('Preview failed', msg, 'error');
    }
  };

  const handleRunReport = () => {
    const useBackground =
      onReportRunQueued != null && onReportRunPatch != null && onReportRunComplete != null;

    if (!useBackground && !onReportRun) {
      showAlert('Not available', 'Report history is only available from the Reports page.', 'info');
      return;
    }
    const err = validateBeforeReport();
    if (err) {
      showAlert('Cannot run', err, 'error');
      return;
    }
    setPreviewError(null);

    const runLabel = selectedRun ? formatRunLabel(selectedRun) : String(filters.payrollRunId);
    const baseParameters = {
      layout,
      payrollRunId: filters.payrollRunId,
      payrollRunLabel: runLabel,
      onlyWarnings: filters.onlyWarnings,
      onlyErrors: filters.onlyErrors,
      lineCategories: filters.lineCategories,
      lineBreakdown: filters.lineBreakdown,
      groupByEmployee: filters.groupByEmployee,
      payPeriodType,
      outputColumnCount: outputColumns.length,
      templateName: templateName.trim() || null,
    };

    if (!useBackground) {
      void (async () => {
        setRunBusy(true);
        try {
          const preview = previewData ?? (await loadPreviewForRun(filters.payrollRunId!));
          const rows = buildReportRows(preview);
          setPreviewRows(rows);
          const { headers, dataRows } = buildExportTable(rows);
          const base = exportBaseFilename();
          const csv = createCSVFile(headers, dataRows, base);
          const excel = createExcelFile(headers, dataRows, base);
          const pdf = createPDFFile(headers, dataRows, base, { title: 'Payroll details report' });
          await onReportRun!({
            headers,
            rows: dataRows,
            recordsProcessed: rows.length,
            csvUrl: csv.url,
            excelUrl: excel.url,
            pdfUrl: pdf.url,
            parameters: {
              ...baseParameters,
              recordsProcessed: rows.length,
            },
          });
          showAlert(
            'Report added to history',
            'This run was saved to Report run history below. Use Details or export buttons on a row to download again.',
            'success'
          );
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : 'Could not run report.';
          setPreviewError(msg);
          showAlert('Run failed', msg, 'error');
        } finally {
          setRunBusy(false);
        }
      })();
      return;
    }

    const localId = `pdr-local-${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    onReportRunQueued!({
      id: localId,
      reportType: 'payroll-details',
      reportName: 'Payroll Details',
      parameters: baseParameters,
      status: 'pending',
      createdAt: new Date(),
    });

    window.setTimeout(() => {
      void (async () => {
        onReportRunPatch!(localId, { status: 'processing' });
        try {
          const preview = previewData ?? (await loadPreviewForRun(filters.payrollRunId!));
          const rows = buildReportRows(preview);
          setPreviewRows(rows);
          const { headers, dataRows } = buildExportTable(rows);
          const base = exportBaseFilename();
          const csv = createCSVFile(headers, dataRows, base);
          const excel = createExcelFile(headers, dataRows, base);
          const pdf = createPDFFile(headers, dataRows, base, { title: 'Payroll details report' });
          await onReportRunComplete!(localId, {
            headers,
            rows: dataRows,
            recordsProcessed: rows.length,
            csvUrl: csv.url,
            excelUrl: excel.url,
            pdfUrl: pdf.url,
            parameters: {
              ...baseParameters,
              recordsProcessed: rows.length,
            },
          });
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : 'Could not run report.';
          setPreviewError(msg);
          onReportRunPatch!(localId, {
            status: 'failed',
            parameters: { ...baseParameters, errorMessage: msg },
          });
        }
      })();
    }, 50);
  };

  const triggerFileDownload = (url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const triggerStoredBlobDownload = (url: string, filename: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const saveTemplateBody = (name: string) => ({
    name,
    layout,
    filters: filtersPayload(),
    columns: columnsPayload(),
    isDefault: templateIsDefault,
  });

  const openCreateTemplateModal = (initialName: string) => {
    setNameModal({
      title: 'Name for report template',
      confirmLabel: 'Save',
      initialValue: initialName,
      onConfirm: async (name) => {
        try {
          const created = await payrollReportTemplatesApi.create(saveTemplateBody(name));
          await refreshTemplates();
          applyTemplateDto(created);
          showAlert('Saved', 'Report template saved.', 'success');
        } catch (e) {
          console.error(e);
          showAlert('Save failed', 'Could not save the report template.', 'error');
        }
      },
    });
  };

  const clearTemplateSelection = () => {
    setSelectedTemplateId('');
    setLoadedTemplateId(null);
    setTemplateName('');
    setTemplateIsDefault(false);
    setLayout('detail');
    setFilters(defaultPayrollFilters());
    setOutputColumns(defaultOutputColumns('detail'));
    setPreviewRows(null);
  };

  const handleTemplateSelectChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    const previousLoadedId = loadedTemplateIdRef.current;
    if (!v) {
      clearTemplateSelection();
      return;
    }
    setSelectedTemplateId(v);
    try {
      const d = await payrollReportTemplatesApi.get(Number(v));
      applyTemplateDto(d);
    } catch (err) {
      console.error(err);
      showAlert('Load failed', 'Could not load the report template.', 'error');
      if (previousLoadedId != null) {
        setSelectedTemplateId(String(previousLoadedId));
        try {
          const d = await payrollReportTemplatesApi.get(previousLoadedId);
          applyTemplateDto(d);
        } catch {
          clearTemplateSelection();
        }
      } else {
        setSelectedTemplateId('');
      }
    }
  };

  const handleSaveTemplate = () => {
    if (!adHoc) return;
    openCreateTemplateModal(templateName);
  };

  const handleSaveAsNewTemplate = () => {
    setNameModal({
      title: 'Name for new report template',
      confirmLabel: 'Save',
      initialValue: '',
      onConfirm: async (name) => {
        try {
          const created = await payrollReportTemplatesApi.create(saveTemplateBody(name));
          await refreshTemplates();
          applyTemplateDto(created);
          showAlert('Created', 'Report template created.', 'success');
        } catch (e) {
          console.error(e);
          showAlert('Save failed', 'Could not save the report template.', 'error');
        }
      },
    });
  };

  const handleUpdateTemplate = async () => {
    if (loadedTemplateId == null) return;
    const name = templateName.trim();
    if (!name) {
      showAlert('Name required', 'Enter a template name before saving.', 'info');
      return;
    }
    try {
      setTemplateSaving(true);
      const updated = await payrollReportTemplatesApi.update(loadedTemplateId, saveTemplateBody(name));
      await refreshTemplates();
      applyTemplateDto(updated);
      showAlert('Saved', 'Report template updated.', 'success');
    } catch (e) {
      console.error(e);
      showAlert('Save failed', 'Could not update the report template.', 'error');
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleDeleteTemplate = () => {
    if (!selectedTemplateId) {
      showAlert('Select a template', 'Choose a saved report template to delete.', 'info');
      return;
    }
    const id = Number(selectedTemplateId);
    const name = templateList.find((x) => x.id === id)?.name ?? 'this template';
    showConfirm(
      'Delete template',
      `Delete saved report template "${name}"? This cannot be undone.`,
      () => {
        void (async () => {
          try {
            await payrollReportTemplatesApi.delete(id);
            await refreshTemplates();
            if (loadedTemplateId === id) clearTemplateSelection();
            else setSelectedTemplateId('');
            showAlert('Deleted', 'The template was removed.', 'success');
          } catch (e) {
            console.error(e);
            showAlert('Delete failed', 'Could not delete the template.', 'error');
          }
        })();
      },
      { danger: true, confirmLabel: 'Delete', cancelLabel: 'Cancel' }
    );
  };

  const addOutputColumn = () => {
    if (!addFieldKey) return;
    if (outputColumns.some((c) => c.fieldKey === addFieldKey)) {
      showAlert('Already added', 'That field is already in the output list.', 'info');
      return;
    }
    setOutputColumns((prev) => [
      ...prev,
      {
        id: newRowId(),
        fieldKey: addFieldKey,
        headerLabel: getDefaultLabelForPayrollOutputField(addFieldKey),
      },
    ]);
    setAddFieldKey('');
  };

  const moveColumn = (index: number, dir: -1 | 1) => {
    setOutputColumns((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const mergePayrollRunWithServerSnapshot = useCallback(async (r: PayrollHistoryItem): Promise<PayrollHistoryItem> => {
    const snap = getPayrollSnapshot(r);
    if (snap?.headers?.length) return r;
    if (r.serverPayrollDetailsRunId == null) return r;
    const d = await payrollDetailsReportRunsApi.get(r.serverPayrollDetailsRunId);
    const snapshot = d.snapshot as { headers?: string[]; dataRows?: string[][] } | undefined;
    return {
      ...r,
      parameters: (d.parameters as Record<string, unknown>) ?? r.parameters,
      payrollDetailsSnapshot: {
        headers: snapshot?.headers ?? [],
        dataRows: snapshot?.dataRows ?? [],
      },
    };
  }, []);

  const openHistoryDetails = useCallback(
    async (r: PayrollHistoryItem) => {
      try {
        const row = await mergePayrollRunWithServerSnapshot(r);
        setDetailHistoryReport((prev) => {
          revokePayrollBlobUrls(prev);
          return row;
        });
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Could not load saved run data.';
        setMessageDialog({ variant: 'alert', title: 'Load failed', message: msg, tone: 'error' });
      }
    },
    [mergePayrollRunWithServerSnapshot]
  );

  const handleHistoryRowExport = useCallback(
    async (kind: 'csv' | 'excel' | 'pdf', r: PayrollHistoryItem) => {
      const baseName = payrollExportBase(r);
      if (kind === 'csv' && r.csvUrl) {
        triggerStoredBlobDownload(r.csvUrl, `${baseName}.csv`);
        return;
      }
      if (kind === 'excel' && r.excelUrl) {
        triggerStoredBlobDownload(r.excelUrl, `${baseName}.xlsx`);
        return;
      }
      if (kind === 'pdf' && r.pdfUrl) {
        triggerStoredBlobDownload(r.pdfUrl, `${baseName}.pdf`);
        return;
      }
      setRowExportBusyId(r.id);
      try {
        const row = await mergePayrollRunWithServerSnapshot(r);
        const snap = getPayrollSnapshot(row);
        if (!snap?.headers?.length) throw new Error('No saved row data for this run.');
        const { headers, dataRows } = snap;
        if (kind === 'csv') {
          const blob = createCSVFile(headers, dataRows, baseName);
          triggerFileDownload(blob.url, blob.filename);
        } else if (kind === 'excel') {
          const blob = createExcelFile(headers, dataRows, baseName);
          triggerFileDownload(blob.url, blob.filename);
        } else {
          const pdf = createPDFFile(headers, dataRows, baseName, { title: 'Payroll details report' });
          triggerFileDownload(pdf.url, pdf.filename);
        }
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Could not build file from saved data.';
        setMessageDialog({ variant: 'alert', title: 'Export failed', message: msg, tone: 'error' });
      } finally {
        setRowExportBusyId(null);
      }
    },
    [mergePayrollRunWithServerSnapshot]
  );

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-neutral-600">Loading…</div>;
  }

  const previewTable = previewRows ? buildExportTable(previewRows) : { headers: [] as string[], dataRows: [] as string[][] };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <NamePromptModal
        open={nameModal != null}
        title={nameModal?.title ?? ''}
        confirmLabel={nameModal?.confirmLabel}
        initialValue={nameModal?.initialValue}
        onCancel={() => setNameModal(null)}
        onConfirm={async (name) => {
          const fn = nameModal?.onConfirm;
          setNameModal(null);
          if (fn) await fn(name);
        }}
      />
      <MessageDialog state={messageDialog} onClose={() => setMessageDialog(null)} />

      {previewModalOpen && previewRows !== null && (
        <div
          className="fixed inset-0 z-[102] flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPreviewModalOpen(false);
          }}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col border border-neutral-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Preview records</h3>
                <p className="text-sm text-neutral-600 mt-0.5">
                  <span className="font-medium text-neutral-800">{previewRows.length}</span> row
                  {previewRows.length === 1 ? '' : 's'} · {layout} layout
                  {layout === 'detail' && (
                    <>
                      {' '}
                      · {filters.lineBreakdown === 'periodTotal'
                        ? `${payPeriodType} totals`
                        : filters.lineBreakdown === 'scheduleDayTypeBreakdown'
                          ? 'Schedule + day type'
                          : 'Schedule breakdown'}
                      {filters.groupByEmployee ? ' · grouped by employee' : ' · flat list'}
                    </>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPreviewModalOpen(false)}
                className="rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100"
                aria-label="Close preview"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-auto flex-1 min-h-0 p-4">
              {previewRows.length === 0 ? (
                <p className="text-sm text-neutral-500">No rows match the current filters.</p>
              ) : (
                <div className="border border-neutral-200 rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50 border-b border-neutral-200 sticky top-0">
                      <tr>
                        {previewTable.headers.map((h, i) => (
                          <th key={i} className="text-left py-2 px-3 font-medium text-neutral-700 whitespace-nowrap">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewTable.dataRows.slice(0, 200).map((row: string[], ri: number) => (
                        <tr key={ri} className="border-b border-neutral-100 last:border-0">
                          {row.map((cell: string, ci: number) => (
                            <td key={ci} className="py-2 px-3 text-neutral-800 whitespace-nowrap max-w-xs truncate">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {previewRows.length > 200 && (
                    <p className="text-xs text-neutral-500 p-2 border-t border-neutral-100">
                      Showing first 200 of {previewRows.length} rows.
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end border-t border-neutral-200 px-4 py-3">
              <button
                type="button"
                onClick={() => setPreviewModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-neutral-800 rounded-md hover:bg-neutral-900"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Payroll details report</h1>
        <p className="text-sm text-neutral-600 mt-1">
          Configure filters and output columns, preview payroll run data, then run the report to save exports to history.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-xs font-semibold text-neutral-600 uppercase tracking-wide">Report template</label>
        <div className="flex flex-wrap items-center gap-1.5">
          <select
            value={selectedTemplateId}
            onChange={(e) => void handleTemplateSelectChange(e)}
            disabled={presetsLoading}
            className="min-w-[200px] flex-1 max-w-md px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white"
          >
            <option value="">Select a saved template…</option>
            {templateList.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
                {p.isDefault ? ' (Default)' : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            title="New report template"
            onClick={() => openCreateTemplateModal('')}
            className="inline-flex items-center justify-center p-2.5 border border-blue-200 bg-blue-50 rounded-md hover:bg-blue-100 text-blue-800"
          >
            <FilePlus className="h-5 w-5" />
          </button>
          <button
            type="button"
            title="Delete template"
            onClick={handleDeleteTemplate}
            className="inline-flex items-center justify-center p-2.5 border border-red-200 rounded-md hover:bg-red-50 text-red-700"
          >
            <Trash2 className="h-5 w-5" />
          </button>
          {showSave && (
            <button
              type="button"
              title="Save template"
              onClick={handleSaveTemplate}
              disabled={templateSaving}
              className="inline-flex items-center justify-center p-2.5 rounded-md bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            >
              {templateSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
            </button>
          )}
          {showSaveAsNew && (
            <>
              <button
                type="button"
                title="Update template"
                onClick={() => void handleUpdateTemplate()}
                disabled={templateSaving}
                className="inline-flex items-center justify-center p-2.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
              >
                {templateSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
              </button>
              <button
                type="button"
                title="Save as new template"
                onClick={handleSaveAsNewTemplate}
                className="inline-flex items-center justify-center p-2.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <Copy className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Template name (for save)"
            className="flex-1 min-w-[12rem] max-w-md px-3 py-2 text-sm border border-neutral-300 rounded-md"
          />
          <label className="inline-flex items-center gap-2 text-sm text-neutral-800 whitespace-nowrap">
            <input
              type="checkbox"
              checked={templateIsDefault}
              onChange={(e) => setTemplateIsDefault(e.target.checked)}
            />
            Is default
          </label>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-neutral-600 uppercase">Layout</span>
            <div className="inline-flex rounded-md border border-neutral-300 overflow-hidden">
              {(['detail', 'summary'] as const).map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => handleLayoutChange(opt)}
                  className={`px-3 py-1.5 text-sm font-medium ${
                    layout === opt ? 'bg-blue-600 text-white' : 'bg-white text-neutral-700 hover:bg-neutral-50'
                  }`}
                >
                  {opt === 'detail' ? 'Detail' : 'Summary'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6 md:items-stretch">
        <div className="w-full md:flex-1 border border-neutral-200 rounded-lg bg-white overflow-hidden">
          <button
            type="button"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="w-full flex items-center gap-2 px-4 py-3 text-left bg-neutral-50 border-b border-neutral-200 hover:bg-neutral-100"
          >
            {filtersExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            <span className="font-semibold text-neutral-900">Filters</span>
          </button>
          {filtersExpanded && (
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">
                  Payroll run <span className="text-red-600">*</span>
                </label>
                <select
                  value={filters.payrollRunId ?? ''}
                  onChange={(e) => {
                    const v = e.target.value ? Number(e.target.value) : null;
                    setFilters((prev) => ({ ...prev, payrollRunId: v, lineCategories: [] }));
                    setPreviewRows(null);
                  }}
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white"
                >
                  <option value="">Select payroll run…</option>
                  {payrollRuns.map((r) => (
                    <option key={r.id} value={r.id}>
                      {formatRunLabel(r)}
                    </option>
                  ))}
                </select>
                {previewLoading && filters.payrollRunId != null && (
                  <p className="text-xs text-neutral-500 mt-1 flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading run preview…
                  </p>
                )}
                {selectedRun && (
                  <p className="text-xs text-neutral-600 mt-1">
                    Pay period: <span className="font-medium">{payPeriodLabel}</span>
                  </p>
                )}
              </div>
              {layout === 'detail' && (
                <div className="space-y-3 rounded-md border border-neutral-200 bg-neutral-50/80 p-3">
                  <p className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Line items</p>
                  <div>
                    <p className="text-xs font-medium text-neutral-700 mb-1.5">Show as</p>
                    <div className="inline-flex rounded-md border border-neutral-300 overflow-hidden">
                      {(
                        [
                          { value: 'periodTotal' as PayrollLineBreakdown, label: `${payPeriodType} total` },
                          { value: 'scheduleBreakdown' as PayrollLineBreakdown, label: 'Schedule breakdown' },
                          { value: 'scheduleDayTypeBreakdown' as PayrollLineBreakdown, label: 'Schedule + day type' },
                        ] as const
                      ).map(({ value, label }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            setFilters((prev) => ({ ...prev, lineBreakdown: value }));
                            setPreviewRows(null);
                          }}
                          className={`px-3 py-1.5 text-sm font-medium ${
                            filters.lineBreakdown === value
                              ? 'bg-blue-600 text-white'
                              : 'bg-white text-neutral-700 hover:bg-neutral-50'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-neutral-500 mt-1">
                      {filters.lineBreakdown === 'periodTotal'
                        ? 'One total row per pay category for the full pay period (weekly, bi-weekly, or monthly based on the run).'
                        : filters.lineBreakdown === 'scheduleDayTypeBreakdown'
                          ? 'Nurse banding: one row per schedule and day type (Mon–Fri, Saturday, Sunday, etc.).'
                          : 'Breakdown by schedule (pay matrix), then holiday pay (if any), Sunday premium, and holiday premium.'}
                    </p>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-neutral-800">
                    <input
                      type="checkbox"
                      checked={filters.groupByEmployee}
                      onChange={(e) => {
                        setFilters((prev) => ({ ...prev, groupByEmployee: e.target.checked }));
                        setPreviewRows(null);
                      }}
                    />
                    Group by employee (show employee name once per block)
                  </label>
                </div>
              )}
              <div className="flex flex-wrap gap-4">
                <label className="inline-flex items-center gap-2 text-sm text-neutral-800">
                  <input
                    type="checkbox"
                    checked={filters.onlyWarnings}
                    onChange={(e) => setFilters((prev) => ({ ...prev, onlyWarnings: e.target.checked }))}
                  />
                  Only employees with warnings
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-neutral-800">
                  <input
                    type="checkbox"
                    checked={filters.onlyErrors}
                    onChange={(e) => setFilters((prev) => ({ ...prev, onlyErrors: e.target.checked }))}
                  />
                  Only employees with errors
                </label>
              </div>
              {layout === 'detail' && lineCategories.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-neutral-700 mb-2">Line categories (optional)</p>
                  <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                    {lineCategories.map((cat) => (
                      <label
                        key={cat}
                        className="inline-flex items-center gap-1.5 px-2 py-1 text-xs border border-neutral-200 rounded-md bg-neutral-50"
                      >
                        <input
                          type="checkbox"
                          checked={filters.lineCategories.includes(cat)}
                          onChange={() => toggleLineCategory(cat)}
                        />
                        {displayLineCategory(cat)}
                      </label>
                    ))}
                  </div>
                  {filters.lineCategories.length === 0 && (
                    <p className="text-xs text-neutral-500 mt-1">No selection = all categories.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="w-full md:flex-1 border border-neutral-200 rounded-lg bg-white overflow-hidden">
          <button
            type="button"
            onClick={() => setColumnsExpanded(!columnsExpanded)}
            className="w-full flex items-center gap-2 px-4 py-3 text-left bg-neutral-50 border-b border-neutral-200 hover:bg-neutral-100"
          >
            {columnsExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            <span className="font-semibold text-neutral-900">Output columns</span>
          </button>
          {columnsExpanded && (
            <div className="p-4 space-y-3">
              <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-neutral-700 mb-1">Add field</label>
                  <select
                    value={addFieldKey}
                    onChange={(e) => setAddFieldKey(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white"
                  >
                    <option value="">Choose a field…</option>
                    {addableFields.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.defaultLabel}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={addOutputColumn}
                  disabled={!addFieldKey}
                  className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
              {outputColumns.length === 0 ? (
                <p className="text-sm text-neutral-500">No output columns yet.</p>
              ) : (
                <div className="border border-neutral-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50 border-b border-neutral-200">
                      <tr>
                        <th className="text-left py-2 px-3 font-medium text-neutral-700 w-20">Order</th>
                        <th className="text-left py-2 px-3 font-medium text-neutral-700">Field</th>
                        <th className="text-left py-2 px-3 font-medium text-neutral-700">Column title</th>
                        <th className="w-10" />
                      </tr>
                    </thead>
                    <tbody>
                      {outputColumns.map((col, index) => (
                        <tr key={col.id} className="border-b border-neutral-100 last:border-0">
                          <td className="py-2 px-3 align-top">
                            <div className="flex gap-0.5">
                              <button
                                type="button"
                                disabled={index === 0}
                                onClick={() => moveColumn(index, -1)}
                                className="p-1 rounded hover:bg-neutral-100 disabled:opacity-30"
                                aria-label="Move up"
                              >
                                <ChevronUp className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                disabled={index === outputColumns.length - 1}
                                onClick={() => moveColumn(index, 1)}
                                className="p-1 rounded hover:bg-neutral-100 disabled:opacity-30"
                                aria-label="Move down"
                              >
                                <ChevronDown className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                          <td className="py-2 px-3 align-top text-neutral-800">
                            {PAYROLL_REPORT_OUTPUT_FIELD_CATALOG.find((c) => c.key === col.fieldKey)?.defaultLabel ||
                              col.fieldKey}
                          </td>
                          <td className="py-2 px-3">
                            <input
                              type="text"
                              value={col.headerLabel}
                              onChange={(e) =>
                                setOutputColumns((prev) =>
                                  prev.map((c) => (c.id === col.id ? { ...c, headerLabel: e.target.value } : c))
                                )
                              }
                              className="w-full px-2 py-1.5 border border-neutral-300 rounded-md text-sm"
                            />
                          </td>
                          <td className="py-2 px-2">
                            <button
                              type="button"
                              onClick={() => setOutputColumns((prev) => prev.filter((c) => c.id !== col.id))}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              aria-label="Remove column"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => void handlePreviewReport()}
            disabled={previewLoading || runBusy}
            className="px-5 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-60"
          >
            Preview report
          </button>
          <button
            type="button"
            onClick={() => void handleRunReport()}
            disabled={previewLoading || runBusy}
            className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-60 inline-flex items-center gap-2"
          >
            {runBusy && <Loader2 className="h-4 w-4 animate-spin" />}
            Run report
          </button>
        </div>
        {previewError && <p className="text-sm text-red-600">{previewError}</p>}
        {previewRows !== null && (
          <p className="text-sm text-neutral-600">
            Last preview / run: <span className="font-medium">{previewRows.length}</span> row
            {previewRows.length === 1 ? '' : 's'}.
          </p>
        )}
      </section>

      {onReportRun != null || onReportRunQueued != null ? (
        <>
          <div className="space-y-2 border-t border-neutral-200 pt-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-neutral-800">Report run history</h3>
              {onClearAllHistory && sortedRunHistory.length > 0 && (
                <button
                  type="button"
                  onClick={() => void onClearAllHistory()}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Clear all history
                </button>
              )}
            </div>
            {sortedRunHistory.length === 0 ? (
              <p className="text-sm text-neutral-500 py-4 border border-dashed border-neutral-200 rounded-lg text-center">
                No runs yet. Select a payroll run and click Run report.
              </p>
            ) : (
              <div className="space-y-2 max-h-[min(60vh,480px)] overflow-y-auto pr-1">
                {sortedRunHistory.map((r) => (
                  <div
                    key={r.id}
                    className="bg-white border border-neutral-200 rounded-lg px-3 py-2.5 flex flex-wrap items-center justify-between gap-2 text-sm"
                  >
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <div className="font-medium text-neutral-900 flex flex-wrap items-center gap-2">
                        <span>{formatCompanyDateTime(r.createdAt)}</span>
                        <ReportRunStatusBadge status={r.status} />
                        {r.status === 'completed' && (
                          <span className="text-neutral-500 font-normal text-xs">
                            ({formatPayrollRunSummary(r)})
                          </span>
                        )}
                      </div>
                      {r.status === 'failed' && r.parameters?.errorMessage ? (
                        <p className="text-xs text-red-700">{String(r.parameters.errorMessage)}</p>
                      ) : null}
                      <div className="text-xs text-neutral-600">
                        Run: {String(r.parameters?.payrollRunLabel ?? r.parameters?.payrollRunId ?? '—')} · Layout:{' '}
                        {String(r.parameters?.layout ?? '—')}
                        {r.parameters?.payPeriodType ? ` · ${String(r.parameters.payPeriodType)}` : ''}
                        {r.parameters?.payPeriodLabel && !r.parameters?.payPeriodType
                          ? ` · ${String(r.parameters.payPeriodLabel)}`
                          : ''}
                        {r.parameters?.lineBreakdown
                          ? ` · ${r.parameters.lineBreakdown === 'scheduleBreakdown' ? 'Schedule breakdown' : 'Period totals'}`
                          : ''}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        disabled={r.status === 'pending' || r.status === 'processing'}
                        onClick={() => void openHistoryDetails(r as PayrollHistoryItem)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-neutral-800 bg-neutral-100 border border-neutral-200 rounded hover:bg-neutral-200 disabled:opacity-40"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        Details
                      </button>
                      {(['csv', 'excel', 'pdf'] as const).map((kind) => (
                        <button
                          key={kind}
                          type="button"
                          disabled={
                            rowExportBusyId === r.id || r.status === 'pending' || r.status === 'processing'
                          }
                          onClick={() => void handleHistoryRowExport(kind, r as PayrollHistoryItem)}
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border disabled:opacity-50 ${
                            kind === 'csv'
                              ? 'text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100'
                              : kind === 'excel'
                                ? 'text-green-700 bg-green-50 border-green-200 hover:bg-green-100'
                                : 'text-rose-700 bg-rose-50 border-rose-200 hover:bg-rose-100'
                          }`}
                        >
                          {rowExportBusyId === r.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : kind === 'csv' ? (
                            <Download className="h-3.5 w-3.5" />
                          ) : kind === 'excel' ? (
                            <FileSpreadsheet className="h-3.5 w-3.5" />
                          ) : (
                            <FileDown className="h-3.5 w-3.5" />
                          )}
                          {kind === 'excel' ? 'Excel' : kind.toUpperCase()}
                        </button>
                      ))}
                      {onDeleteReport && (
                        <button
                          type="button"
                          onClick={() => onDeleteReport(r.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {detailHistoryReport && (
            <div
              className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-4 bg-black/40"
              role="dialog"
              aria-modal="true"
              onClick={() => {
                setDetailHistoryReport((prev) => {
                  revokePayrollBlobUrls(prev);
                  return null;
                });
              }}
            >
              <div
                className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col border border-neutral-200 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-neutral-200 bg-neutral-50 shrink-0">
                  <div>
                    <h2 className="text-base font-semibold text-neutral-900">Payroll details — report run</h2>
                    <p className="text-xs text-neutral-600 mt-1">
                      Run: {formatCompanyDateTime(detailHistoryReport.createdAt)} ·{' '}
                      {formatPayrollRunSummary(detailHistoryReport)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setDetailHistoryReport((prev) => {
                        revokePayrollBlobUrls(prev);
                        return null;
                      })
                    }
                    className="rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="overflow-auto flex-1 p-4">
                  {(() => {
                    const snap = getPayrollSnapshot(detailHistoryReport);
                    if (!snap?.headers?.length) {
                      return <p className="text-sm text-neutral-500">No saved data for this run.</p>;
                    }
                    return (
                      <div className="border border-neutral-200 rounded-lg overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-neutral-50 border-b border-neutral-200 sticky top-0">
                            <tr>
                              {snap.headers.map((h, i) => (
                                <th key={i} className="text-left py-2 px-3 font-medium text-neutral-700 whitespace-nowrap">
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {snap.dataRows.slice(0, 300).map((row, ri) => (
                              <tr key={ri} className="border-b border-neutral-100">
                                {row.map((cell, ci) => (
                                  <td key={ci} className="py-2 px-3 whitespace-nowrap max-w-xs truncate">
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default PayrollDetailsReport;
